import { createDeal, dealsApi, editDealInfo } from "@/api/deals-api";
import { filesApi } from "@/api/files-api";
import { companySearchApi } from "@/api/company-search-api";
import { getUrlValidationError, getLinkedInUrlValidationError } from "@/lib/url-utils";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUser } from "@clerk/clerk-react";
import { useDealsListStore } from "@/store/deals-list-store";
import { useWorkspaces } from "@/store/workspace-store";
import { getErrorMessage } from "@/lib/api-error";
import { Plus, Upload, X, FileText, Loader2, Globe, Linkedin, Eye, StickyNote } from "lucide-react";
import { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { GoogleDrivePicker } from "@/components/portal/google-drive-picker";
import { DocSendPicker } from "@/components/common/docsend-picker";
import { ExternalLink } from "lucide-react";
const generateId = () =>
  Math.random().toString(36).substring(2, 9) + Date.now().toString(36);
import { FILE_TYPE_MAPPING } from "@/lib/utils";
import { googleTokenHeader } from "@/lib/google-drive";
import { axiosInstance } from "@/lib/axios-instance";
import { Badge } from "@/components/ui/badge";
import { trackFileUpload } from "@/lib/analytics/file-upload-tracking";
import { FilePreviewDialog } from "@/components/common/file-preview-dialog";
import { IInboundEmailAttachment } from "@/types/data-models";
import { inboxApi } from "@/api/inbox-api";

interface WebSearchResult {
  url: string;
  title?: string;
  description?: string;
  favicon?: string;
}

interface LinkedInSearchResult {
  url: string;
  title?: string;
  description?: string;
  favicon?: string;
}

interface AiSuggestion {
  url?: string;
  title?: string;
  description?: string;
  favicon?: string;
}

// The AI search service returns either a flat array of suggestion objects
// (old shape) or an array of field wrappers `{ field_name, suggestions: [...] }`
// (new shape). Normalize both into a flat suggestion list.
function extractSuggestions(response: any, fieldName: string): AiSuggestion[] {
  const results = response?.results ?? response?.data?.results;
  if (!Array.isArray(results) || results.length === 0) return [];

  if (results[0] && typeof results[0].url === "string") {
    return results as AiSuggestion[];
  }

  if (results[0] && Array.isArray(results[0].suggestions)) {
    const match = results.find((r: any) => r?.field_name === fieldName);
    if (match && Array.isArray(match.suggestions)) {
      return match.suggestions as AiSuggestion[];
    }
    return results.flatMap((r: any) =>
      Array.isArray(r?.suggestions) ? (r.suggestions as AiSuggestion[]) : []
    );
  }

  return [];
}

interface DealFile {
  id: string;
  source: "local" | "drive" | "docsend" | "email";
  file?: File;
  driveMetadata?: any;
  docsendCredentials?: {
    url: string;
    email: string;
    passcode?: string;
  };
  // Email attachments are already uploaded (they have a FileUpload doc), so
  // they're not re-uploaded on submit — instead their id + chosen type are
  // handed back via onDealCreated so the caller can re-parent them onto the deal.
  fileUploadId?: string;
  fileUrl?: string;
  name: string;
  type: string;
  size: number;
  mimeType?: string;
}

interface AddDealDialogProps {
  open?: boolean;
  addButtonClasses?: string;
  showTrigger?: boolean;
  onOpenChange?: (open: boolean) => void;
  onDealCreated?: (companyId: string) => void;
  initialValues?: {
    companyName?: string;
    website?: string;
  };
  // Where the deal came from, when the caller already knows. The inbox does:
  // an email that arrived at the workspace address is an email referral, and
  // making the user re-pick "Email" from a 16-item list was the complaint.
  defaultSource?: string;
  // Already-uploaded attachments (e.g. from an inbox email) to pre-load into
  // the Documents list so they're typed alongside any newly uploaded files.
  emailAttachments?: IInboundEmailAttachment[];
  // Inbox email these attachments came from. When set, the typed attachments
  // are bound to the new company (before the application is created) so they
  // reach the report sequence — see attachFilesToCompany.
  emailId?: string;
}

type SuggestionItem = {
  url: string;
  title?: string;
  description?: string;
  favicon?: string;
};

// Rich suggestion list shared by the Website and LinkedIn fields: a
// "Suggested" header, then rows with a circular logo, the name, and the
// URL underneath. The full description shows in a tooltip on hover.
function SuggestionDropdown({
  results,
  isLoading,
  variant,
  onSelect,
}: {
  results: SuggestionItem[];
  isLoading: boolean;
  variant: "website" | "linkedin";
  onSelect: (result: SuggestionItem) => void;
}) {
  const FallbackIcon = variant === "linkedin" ? Linkedin : Globe;
  const fallbackIconClass =
    variant === "linkedin" ? "text-blue-600" : "text-blue-500";

  return (
    <div
      className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg"
      onMouseDown={(e) => e.stopPropagation()}
    >
      {isLoading ? (
        <div className="flex items-center justify-center p-3">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          <span className="text-sm text-muted-foreground">Searching...</span>
        </div>
      ) : (
        <TooltipProvider delayDuration={300}>
          <div className="p-1">
            <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
              Suggested
            </div>
            <div className="max-h-[240px] overflow-y-auto">
              {results.map((result, index) => {
                const name =
                  result.title ||
                  (variant === "linkedin" ? "LinkedIn Profile" : result.url);
                return (
                  // Row, not a single button: the "open" affordance is an
                  // anchor, and an <a> cannot legally nest inside a <button>.
                  <div
                    key={index}
                    className="flex items-center rounded-md hover:bg-muted transition-colors"
                  >
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="min-w-0 flex-1 flex items-center gap-2.5 px-2 py-1.5 text-left"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => onSelect(result)}
                        >
                          {result.favicon ? (
                            <img
                              src={result.favicon}
                              alt=""
                              className="h-7 w-7 shrink-0 rounded-full border bg-white object-contain p-0.5"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border bg-white">
                              <FallbackIcon className={`h-4 w-4 ${fallbackIconClass}`} />
                            </span>
                          )}
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium">
                              {name}
                            </span>
                            <span className="block truncate text-xs text-muted-foreground">
                              {result.url}
                            </span>
                          </span>
                        </button>
                      </TooltipTrigger>
                      {result.description && (
                        <TooltipContent side="right" align="start" className="max-w-sm">
                          <p>{result.description}</p>
                          <p className="mt-1 opacity-70">{result.url}</p>
                        </TooltipContent>
                      )}
                    </Tooltip>

                    {/* Open the candidate without picking it. Several results
                        often share a company name, and the only way to tell
                        them apart is to look. preventDefault on mousedown
                        stops the input blurring, which would close this
                        dropdown before the click landed. */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <a
                          href={result.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`Open ${name} in a new tab`}
                          className="mr-1 shrink-0 rounded p-1.5 text-muted-foreground hover:bg-background hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </TooltipTrigger>
                      <TooltipContent side="top">Open in a new tab</TooltipContent>
                    </Tooltip>
                  </div>
                );
              })}
            </div>
          </div>
        </TooltipProvider>
      )}
    </div>
  );
}

export function AddDealDialog({
  open,
  addButtonClasses,
  showTrigger,
  onOpenChange,
  onDealCreated,
  initialValues,
  defaultSource,
  emailAttachments,
  emailId,
}: AddDealDialogProps) {
  const [companyName, setCompanyName] = useState(initialValues?.companyName || "");
  const [companyWebsite, setCompanyWebsite] = useState(initialValues?.website || "");
  const [linkedInUrl, setLinkedInUrl] = useState("");
  // Free-form capture at intake. Written as a deal note once the application
  // exists — see handleAddDeal, where the ordering is forced.
  const [notes, setNotes] = useState("");
  const [files, setFiles] = useState<DealFile[]>([]);
  const { activeWorkspaceId, activeWorkspace } = useWorkspaces();
  const { user } = useUser();
  const [progress, setProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { refreshDeals } = useDealsListStore();
  const [selectedSource, setSelectedSource] = useState(defaultSource ?? "Manual");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [isDrivePickerOpen, setIsDrivePickerOpen] = useState(false);
  const [isDocSendPickerOpen, setIsDocSendPickerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("local");

  const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024; // 25MB

  const [errors, setErrors] = useState<{ website?: string; linkedin?: string }>({});
  const fileListRef = useRef<HTMLDivElement>(null);

  // Company search state - separate for web and LinkedIn
  const [isSearchingWeb, setIsSearchingWeb] = useState(false);
  const [isSearchingLinkedIn, setIsSearchingLinkedIn] = useState(false);
  const [webResults, setWebResults] = useState<WebSearchResult[]>([]);
  const [linkedInResults, setLinkedInResults] = useState<LinkedInSearchResult[]>([]);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [webDropdownHidden, setWebDropdownHidden] = useState(false);
  const [linkedInDropdownHidden, setLinkedInDropdownHidden] = useState(false);

  // Seeded once per open, like the attachments below: callers build
  // `initialValues` inline, so re-running on its identity would overwrite what
  // the user has typed on every parent re-render.
  useEffect(() => {
    if (!open) return;
    if (initialValues?.companyName) setCompanyName(initialValues.companyName);
    if (initialValues?.website) setCompanyWebsite(initialValues.website);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Pre-load any email attachments into the Documents list when the dialog
  // opens so they're typed alongside newly uploaded files. Seeded once per
  // open — re-running on attachment identity changes would wipe selections.
  useEffect(() => {
    if (!open) return;
    setFiles(
      (emailAttachments ?? [])
        .filter((att) => att.file_upload_id)
        .map((att) => ({
          id: att.file_upload_id!,
          source: "email" as const,
          fileUploadId: att.file_upload_id,
          fileUrl: att.file_url,
          name: att.file_name,
          type: "",
          size: att.file_size ?? 0,
          mimeType: att.content_type,
        })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const validateWebsite = (url: string) => {
    const error = getUrlValidationError(url);
    setErrors((prev) => ({ ...prev, website: error || undefined }));
    return !error;
  };

  const validateLinkedIn = (url: string) => {
    const error = getLinkedInUrlValidationError(url);
    setErrors((prev) => ({ ...prev, linkedin: error || undefined }));
    return !error;
  };

  // Search web for company websites
  const performWebSearch = useCallback(async (searchTerm: string) => {
    if (!searchTerm.trim() || searchTerm.trim().length < 2) {
      return;
    }

    setIsSearchingWeb(true);

    try {
      const response = await companySearchApi.searchWeb(searchTerm);
      const suggestions = extractSuggestions(response, "website");

      if (suggestions.length > 0) {
        const webOnlyResults = suggestions
          .filter((s) => s.url && !s.url.includes("linkedin.com"))
          .slice(0, 10)
          .map((s) => ({
            url: s.url!,
            title: s.title?.split(" | ")[0]?.split(" - ")[0]?.trim(),
            description: s.description,
            favicon: s.favicon,
          }));
        setWebResults(webOnlyResults);
        setWebDropdownHidden(false);
      } else {
        setWebResults([]);
      }
      setHasSearched(true);
    } catch (error) {
      console.error("Web search error:", error);
      setWebResults([]);
    } finally {
      setIsSearchingWeb(false);
    }
  }, []);

  // Search LinkedIn - can be called with or without website URL for enrichment
  const performLinkedInSearch = useCallback(async (searchTerm: string, websiteUrl?: string) => {
    if (!searchTerm.trim() || searchTerm.trim().length < 2) {
      return;
    }

    setIsSearchingLinkedIn(true);

    try {
      const response = await companySearchApi.searchLinkedIn(searchTerm, websiteUrl);
      const suggestions = extractSuggestions(response, "linkedIn");

      if (suggestions.length > 0) {
        const linkedInOnlyResults = suggestions
          .filter((s) => s.url?.includes("linkedin.com"))
          .slice(0, 10)
          .map((s) => ({
            url: s.url!,
            title: s.title?.split(" | ")[0]?.trim(),
            description: s.description,
            favicon: s.favicon,
          }));
        setLinkedInResults(linkedInOnlyResults);
        setLinkedInDropdownHidden(false);
      } else {
        setLinkedInResults([]);
      }
    } catch (error) {
      console.error("LinkedIn search error:", error);
      setLinkedInResults([]);
    } finally {
      setIsSearchingLinkedIn(false);
    }
  }, []);

  // Debounced search effect - triggers 500ms after user stops typing company name
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Don't search if name is too short
    if (companyName.trim().length < 2) {
      return;
    }

    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      // Only search web if website field is empty
      if (!companyWebsite) {
        performWebSearch(companyName);
      }
      // Only search LinkedIn if LinkedIn field is empty
      if (!linkedInUrl) {
        performLinkedInSearch(companyName);
      }
    }, 500);

    // Cleanup on unmount
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyName]); // Only trigger on company name change, not on field selection

  // Handle web result selection
  const handleWebResultSelect = (result: WebSearchResult) => {
    setCompanyWebsite(result.url);
    if (result.title && !companyName) {
      setCompanyName(result.title);
    }
    setWebResults([]); // Clear results to close dropdown
    toast.success("Website selected");
    // LinkedIn search will auto-refresh via useEffect when companyWebsite changes
  };

  // Handle LinkedIn result selection
  const handleLinkedInResultSelect = (result: LinkedInSearchResult) => {
    setLinkedInUrl(result.url);
    if (result.title && !companyName) {
      setCompanyName(result.title);
    }
    setLinkedInResults([]); // Clear results to close dropdown
    toast.success("LinkedIn selected");
  };

  // Auto-scroll to file list when files are added
  useEffect(() => {
    if (files.length > 0 && fileListRef.current) {
      fileListRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [files.length]);

  const handleFileUpload = (
    event: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLDivElement>
  ) => {
    let fileList: FileList | null = null;

    if ("dataTransfer" in event) {
      event.preventDefault();
      fileList = event.dataTransfer.files;
    } else {
      fileList = event.target.files;
    }

    if (fileList && fileList.length > 0) {
      const newFiles: DealFile[] = [];
      Array.from(fileList).forEach((file) => {
        if (file.size > MAX_FILE_SIZE_BYTES) {
          toast.error(`File ${file.name} exceeds 25MB limit.`);
          return;
        }
        newFiles.push({
          id: generateId(),
          source: "local",
          file: file,
          name: file.name,
          type: "", // User must select
          size: file.size,
          mimeType: file.type,
        });
      });
      setFiles((prev) => [...prev, ...newFiles]);
      toast.success(`${newFiles.length} file(s) added`);
    }
    // Reset input
    if (event.target instanceof HTMLInputElement) {
      event.target.value = "";
    }
  };

  const handleDriveSelection = (
    selectedDriveFiles: any[],
    accessToken: string
  ) => {
    const newFiles: DealFile[] = selectedDriveFiles.map((f) => ({
      id: generateId(),
      source: "drive",
      driveMetadata: { ...f, accessToken },
      name: f.name,
      type: "", // User must select
      size: Number(f.size) || 0,
      mimeType: f.mimeType,
    }));
    setFiles((prev) => [...prev, ...newFiles]);
    toast.success(`${newFiles.length} file(s) imported from Drive`);
  };

  const handleDocSendCredentials = (credentials: {
    url: string;
    email: string;
    passcode?: string;
    fileType?: string;
  }) => {
    // Extract a display name from the URL (supports both /view/ and /v/ patterns)
    const urlMatch = credentials.url.match(/\/(view|v)\/([a-zA-Z0-9]+)/);
    const docId = urlMatch ? urlMatch[2] : "document";
    const displayName = `DocSend - ${docId}.pdf`;

    const newFile: DealFile = {
      id: generateId(),
      source: "docsend",
      docsendCredentials: {
        url: credentials.url,
        email: credentials.email,
        passcode: credentials.passcode,
      },
      name: displayName,
      type: credentials.fileType || "", // User may have selected a type
      size: 0, // Unknown until imported
      mimeType: "application/pdf",
    };
    setFiles((prev) => [...prev, newFile]);
    toast.success("DocSend link added. It will be imported when you create the deal.");
  };

  const removeFile = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const updateFileType = (id: string, type: string) => {
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, type } : f)));
  };

  const clearFields = () => {
    setCompanyName("");
    setCompanyWebsite("");
    setLinkedInUrl("");
    setNotes("");
    setFiles([]);
    setSelectedSource(defaultSource ?? "Manual");
    setSelectedStatus("");
    setActiveTab("local");
    setErrors({});
    setWebResults([]);
    setLinkedInResults([]);
    setHasSearched(false);
  };

  const handleAddDeal = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (!user?.id) {
      toast.error("Unable to identify user. Please try logging in again.");
      return;
    }

    if (!activeWorkspaceId) {
      toast.error("No workspace selected. Please select a workspace.");
      return;
    }

    // Validate website URL if provided
    let isWebsiteValid = true;
    if (companyWebsite) {
      isWebsiteValid = validateWebsite(companyWebsite);
    }

    // Validate LinkedIn URL if provided
    let isLinkedInValid = true;
    if (linkedInUrl) {
      isLinkedInValid = validateLinkedIn(linkedInUrl);
    }

    if (!isWebsiteValid || !isLinkedInValid) {
      toast.error("Please fix the errors in the form before submitting.");
      return;
    }

    const userId = user.id;
    setIsLoading(true);
    setProgress(0);

    try {
      // Step 1: Create company (25%)
      setProgress(5);
      const createCompanyPitchboxProps = {
        userId,
        workspaceId: activeWorkspaceId,
        companyName,
        companyWebsite,
      };

      const createCompanyResponse = await createDeal.createCompanyPitchbox(
        createCompanyPitchboxProps
      );

      // Any response without a usable company id is a failure, whatever the
      // status says — reading `.result._id` blind surfaces a raw TypeError to
      // the user instead of the backend's message.
      if (!createCompanyResponse?.result?._id) {
        toast.error(
          createCompanyResponse?.message ||
            "Failed to create company. Please try again or contact support."
        );
        return;
      }

      const companyId = createCompanyResponse.result._id;

      setProgress(25);

      // Email attachments already have a FileUpload doc, so they're re-parented
      // onto the deal by the caller (via emailFileTypes) rather than re-uploaded.
      const uploadFiles = files.filter((f) => f.source !== "email");
      const emailFileTypes: Record<string, string> = {};
      for (const f of files) {
        if (f.source === "email" && f.fileUploadId && f.type) {
          emailFileTypes[f.fileUploadId] = f.type;
        }
      }

      // Bind email attachments to the company BEFORE creating the application —
      // the report sequence is seeded from the company's files at creation time,
      // so binding afterwards would miss it. Non-fatal: a failure still creates
      // the deal (the user can link the attachments later from the inbox).
      if (emailId && Object.keys(emailFileTypes).length) {
        try {
          await inboxApi.attachFilesToCompany(emailId, companyId, emailFileTypes);
        } catch (err) {
          console.error("Failed to bind email attachments to company", err);
          toast.error(
            "Couldn't attach the email files. The deal was created without them — add them from the Data Room and regenerate the report."
          );
        }
      }

      // Step 2: Handle Files (25% - 60%) - Upload before creating application so sequence has files
      if (uploadFiles.length > 0) {
        const totalFiles = uploadFiles.length;
        let processedFiles = 0;

        const uploadSource = uploadFiles[0].source === "drive" ? "google_drive" as const
          : uploadFiles[0].source === "docsend" ? "docsend" as const
          : "local" as const;

        trackFileUpload({
          action: "started",
          upload_source: uploadSource,
          context: "add_deal",
          file_count: totalFiles,
          workspace_id: activeWorkspaceId,
          opportunity_application_id: companyId,
          user_id: userId,
        });

        for (const file of uploadFiles) {
          const fileSource = file.source === "drive" ? "google_drive" as const
            : file.source === "docsend" ? "docsend" as const
            : "local" as const;

          try {
            if (file.source === "local" && file.file) {
              await filesApi.uploadFile(
                {
                  file: file.file,
                  file_name: file.name, // or file.type if unique
                  file_type: file.type || "company pitchdeck", // default or selected
                  object_ref: "company", // Keep as company for consistency? Or opportunity?
                  object_ref_value: companyId,
                },
                companyId,
                activeWorkspaceId || ""
              );
            } else if (file.source === "drive") {
              // Call import API
              await axiosInstance.post(
                "/google-drive/import-file",
                {
                  company_id: companyId,
                  opportunity_id: activeWorkspaceId,
                  file_id: file.driveMetadata?.id,
                  file_type: file.type || "company pitchdeck",
                },
                {
                  headers: googleTokenHeader(file.driveMetadata?.accessToken),
                }
              );
            } else if (file.source === "docsend" && file.docsendCredentials) {
              // Import from DocSend
              await axiosInstance.post("/docsend/import-file", {
                company_id: companyId,
                opportunity_id: activeWorkspaceId,
                url: file.docsendCredentials.url,
                email: file.docsendCredentials.email,
                passcode: file.docsendCredentials.passcode,
                file_type: file.type || "company pitchdeck",
              });
            }

            trackFileUpload({
              action: "success",
              upload_source: fileSource,
              context: "add_deal",
              file_count: 1,
              workspace_id: activeWorkspaceId,
              opportunity_application_id: companyId,
              user_id: userId,
            });
          } catch (err) {
            console.error(`Failed to process file ${file.name}`, err);

            const isNetwork = err instanceof Error && err.message.includes("Network Error");
            trackFileUpload({
              action: "failed",
              upload_source: fileSource,
              context: "add_deal",
              file_count: 1,
              workspace_id: activeWorkspaceId,
              opportunity_application_id: companyId,
              user_id: userId,
              failure_reason: isNetwork ? "network_error" : "server_error",
            });
          }
          processedFiles++;
          setProgress(25 + (processedFiles / totalFiles) * 35);
        }
      }

      setProgress(60);

      // Step 3: Create opportunity application (60% - 80%) - After files so sequence generation has them
      await createDeal.createOpportunityApplication(
        companyId,
        activeWorkspaceId,
        selectedStatus,
        selectedSource
      );

      setProgress(80);

      // Step 3b: Save the intake note.
      //
      // Must run after createOpportunityApplication: add_deal_note resolves the
      // OpportunityApplication by (applicant_company, pertains_to_opportunity),
      // so there is nothing to attach to until the application exists.
      //
      // Non-blocking, like the LinkedIn write below — the deal is already
      // created by this point, and failing the whole flow over a note would
      // leave the user with a deal they were told had not been made.
      if (notes.trim()) {
        try {
          await dealsApi.addDealNote(
            companyId,
            notes.trim(),
            activeWorkspaceId,
            user.id
          );
        } catch (error) {
          console.error("Failed to save the intake note:", error);
          toast.warning(
            "Deal created, but the note could not be saved. You can add it from the deal page."
          );
        }
      }

      // Step 4: Add LinkedIn URL (80% - 100%)
      if (linkedInUrl && linkedInUrl.trim() !== "") {
        setProgress(90);
        try {
          await editDealInfo.editDealInfo({
            workspaceId: activeWorkspaceId,
            companyId: companyId,
            fieldName: "linkedIn",
            fieldValue: linkedInUrl.trim(),
          });
        } catch (error) {
          // Non-blocking error - LinkedIn URL addition failed but deal was created
          console.error("Failed to add LinkedIn URL:", error);
        }
      }

      setProgress(100);

      clearFields();
      refreshDeals(activeWorkspaceId);
      toast.success("Deal added successfully!");
      onDealCreated?.(companyId);
      onOpenChange?.(false);
    } catch (error) {
      toast.error(
        getErrorMessage(
          error,
          "Failed to add deal. Please try again or contact support."
        )
      );
      console.error(error);
    } finally {
      setIsLoading(false);
      setProgress(0);
    }
  };

  // Add this effect to clear fields when dialog closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      clearFields();
    }
    onOpenChange?.(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {showTrigger && (
        <DialogTrigger asChild>
          <Button
            size="sm"
            className={`${addButtonClasses}`}
            data-tour="add-deal-button"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Deal
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Add Deal</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 px-1" onMouseDown={() => {
          setWebDropdownHidden(true);
          setLinkedInDropdownHidden(true);
        }}>
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="company-name">Company Name*</Label>
              <Input
                id="company-name"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Start typing to search..."
                required
                autoComplete="off"
              />
              {hasSearched && webResults.length === 0 && linkedInResults.length === 0 && companyName.trim().length >= 2 && !isSearchingWeb && !isSearchingLinkedIn && !companyWebsite && !linkedInUrl && (
                <p className="text-xs text-muted-foreground">No results found - enter details manually</p>
              )}
            </div>

            {/* Website & LinkedIn side-by-side with independent dropdowns */}
            <div className="grid grid-cols-2 gap-4">
              {/* Website field with dropdown */}
              <div className="space-y-2">
                <Label htmlFor="company-website" className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-blue-500" />
                  Company Website
                </Label>
                <div className="relative" onMouseDown={(e) => e.stopPropagation()}>
                  <Input
                    id="company-website"
                    value={companyWebsite}
                    onChange={(e) => {
                      setCompanyWebsite(e.target.value);
                      if (errors.website) validateWebsite(e.target.value);
                      // Clear results when manually typing
                      setWebResults([]);
                    }}
                    onFocus={() => {
                      setWebDropdownHidden(false);
                      if (webResults.length === 0 && !companyWebsite && companyName.trim().length >= 2) {
                        performWebSearch(companyName);
                      }
                    }}
                    onBlur={(e) => {
                      validateWebsite(e.target.value);
                      setWebDropdownHidden(true);
                    }}
                    placeholder="https://"
                    className={errors.website ? "border-red-500 focus-visible:ring-red-500" : ""}
                  />
                  {isSearchingWeb && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    </div>
                  )}

                  {/* Web search dropdown - only show if field is empty and not dismissed */}
                  {(webResults.length > 0 || isSearchingWeb) && !companyWebsite && !webDropdownHidden && (
                    <SuggestionDropdown
                      results={webResults}
                      isLoading={isSearchingWeb}
                      variant="website"
                      onSelect={handleWebResultSelect}
                    />
                  )}
                </div>
                {errors.website && (
                  <p className="text-xs text-red-500">{errors.website}</p>
                )}
              </div>

              {/* LinkedIn field with dropdown */}
              <div className="space-y-2">
                <Label htmlFor="linkedin-url" className="flex items-center gap-2">
                  <Linkedin className="h-4 w-4 text-blue-600" />
                  LinkedIn URL
                </Label>
                <div className="relative" onMouseDown={(e) => e.stopPropagation()}>
                  <Input
                    id="linkedin-url"
                    value={linkedInUrl}
                    onChange={(e) => {
                      setLinkedInUrl(e.target.value);
                      if (errors.linkedin) validateLinkedIn(e.target.value);
                      // Clear results when manually typing
                      setLinkedInResults([]);
                    }}
                    onFocus={() => {
                      setLinkedInDropdownHidden(false);
                      if (linkedInResults.length === 0 && !linkedInUrl && companyName.trim().length >= 2) {
                        performLinkedInSearch(companyName);
                      }
                    }}
                    onBlur={(e) => {
                      const val = e.target.value.trim();
                      if (val) {
                        validateLinkedIn(val);
                      } else {
                        setErrors((prev) => ({ ...prev, linkedin: undefined }));
                      }
                      setLinkedInDropdownHidden(true);
                    }}
                    placeholder="https://linkedin.com/company/..."
                    className={errors.linkedin ? "border-red-500 focus-visible:ring-red-500" : ""}
                  />
                  {isSearchingLinkedIn && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    </div>
                  )}

                  {/* LinkedIn search dropdown - only show if field is empty and not dismissed */}
                  {(linkedInResults.length > 0 || isSearchingLinkedIn) && !linkedInUrl && !linkedInDropdownHidden && (
                    <SuggestionDropdown
                      results={linkedInResults}
                      isLoading={isSearchingLinkedIn}
                      variant="linkedin"
                      onSelect={handleLinkedInResultSelect}
                    />
                  )}
                </div>
                {errors.linkedin && (
                  <p className="text-xs text-red-500">{errors.linkedin}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="source">Source</Label>
                <Select
                  value={selectedSource}
                  onValueChange={setSelectedSource}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Submission">Submission</SelectItem>
                    <SelectItem value="Email">Email</SelectItem>
                    <SelectItem value="Intro">Intro</SelectItem>
                    <SelectItem value="Referral">Referral</SelectItem>
                    <SelectItem value="CoInvestor">CoInvestor</SelectItem>
                    <SelectItem value="Angel">Angel</SelectItem>
                    <SelectItem value="LP">LP</SelectItem>
                    <SelectItem value="Accelerator">Accelerator</SelectItem>
                    <SelectItem value="Conference">Conference</SelectItem>
                    <SelectItem value="Platform">Platform</SelectItem>
                    <SelectItem value="Advisor">Advisor</SelectItem>
                    <SelectItem value="TechTransfer">TechTransfer</SelectItem>
                    <SelectItem value="Grant">Grant</SelectItem>
                    <SelectItem value="Social">Social</SelectItem>
                    <SelectItem value="Media">Media</SelectItem>
                    <SelectItem value="Manual">Manual</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={selectedStatus}
                  onValueChange={setSelectedStatus}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeWorkspace?.statuses.map((status) => (
                      <SelectItem key={status.title} value={status.title}>
                        {status.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>
                Attach{" "}
                <Badge variant="secondary" className="ml-2 text-[10px] h-5">
                  Optional
                </Badge>
              </Label>
              <p className="text-xs text-muted-foreground mb-2">
                Pitch decks, financials and other materials — or anything you
                already know about the company
              </p>

              <Tabs
                value={activeTab}
                onValueChange={setActiveTab}
                className="w-full"
              >
                <TabsList className="grid w-full grid-cols-4 mb-4">
                  <TabsTrigger
                    value="local"
                    className="flex items-center gap-2"
                  >
                    <FileText className="h-4 w-4" /> Local Files
                  </TabsTrigger>
                  <TabsTrigger
                    value="drive"
                    className="flex items-center gap-2"
                  >
                    {/* Google Drive Icon SVG */}
                    <svg
                      viewBox="0 0 87.3 78"
                      className="h-4 w-4"
                      aria-hidden="true"
                    >
                      <path
                        d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z"
                        fill="#0066da"
                      />
                      <path
                        d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z"
                        fill="#00ac47"
                      />
                      <path
                        d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z"
                        fill="#ea4335"
                      />
                      <path
                        d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z"
                        fill="#00832d"
                      />
                      <path
                        d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z"
                        fill="#2684fc"
                      />
                      <path
                        d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z"
                        fill="#ffba00"
                      />
                    </svg>
                    Google Drive
                  </TabsTrigger>
                  <TabsTrigger
                    value="docsend"
                    className="flex items-center gap-2"
                  >
                    <ExternalLink className="h-4 w-4" />
                    DocSend
                  </TabsTrigger>
                  <TabsTrigger
                    value="notes"
                    className="flex items-center gap-2"
                  >
                    <StickyNote className="h-4 w-4" /> Notes
                    {/* Notes live behind a tab, so signal when there's
                        something in there — otherwise it's invisible. */}
                    {notes.trim() && (
                      <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                    )}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="notes" className="mt-0">
                  <Textarea
                    id="deal-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Intro from a founder, context from a call, why this is interesting..."
                    rows={6}
                  />
                  <p className="mt-2 text-xs text-muted-foreground">
                    Saved as the first note on the deal.
                  </p>
                </TabsContent>

                <TabsContent value="local" className="mt-0">
                  <div
                    className="relative border-2 border-dashed rounded-lg p-8 border-gray-200 hover:border-primary/50 transition-colors bg-gray-50/50"
                    onDragOver={(e) => {
                      e.preventDefault();
                    }}
                    onDrop={handleFileUpload}
                  >
                    <input
                      type="file"
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      onChange={handleFileUpload}
                      accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt"
                      multiple
                    />

                    <div className="flex flex-col items-center">
                      <div className="bg-white p-3 rounded-full shadow-sm mb-3">
                        <Upload className="h-6 w-6 text-primary" />
                      </div>
                      <div className="text-sm text-center space-y-1">
                        <p className="font-medium text-gray-900">
                          Drag and drop files here or click to browse
                        </p>
                        <p className="text-muted-foreground text-xs">
                          PDF, DOC, XLSX, CSV, PPT, etc.
                        </p>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="drive" className="mt-0">
                  <div className="border-2 border-dashed rounded-lg p-8 border-gray-200 bg-gray-50/50 flex flex-col items-center justify-center text-center">
                    <div className="bg-white p-3 rounded-full shadow-sm mb-3">
                      <svg
                        viewBox="0 0 87.3 78"
                        className="h-6 w-6"
                        aria-hidden="true"
                      >
                        <path
                          d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z"
                          fill="#0066da"
                        />
                        <path
                          d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z"
                          fill="#00ac47"
                        />
                        <path
                          d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z"
                          fill="#ea4335"
                        />
                        <path
                          d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z"
                          fill="#00832d"
                        />
                        <path
                          d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z"
                          fill="#2684fc"
                        />
                        <path
                          d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z"
                          fill="#ffba00"
                        />
                      </svg>
                    </div>
                    <h3 className="font-medium text-gray-900 mb-1">
                      Import from Google Drive
                    </h3>
                    <p className="text-xs text-muted-foreground mb-4 max-w-xs">
                      Connect your account to select files directly from your
                      Google Drive.
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => setIsDrivePickerOpen(true)}
                      className="gap-2"
                    >
                      Browse Google Drive
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="docsend" className="mt-0">
                  <div className="border-2 border-dashed rounded-lg p-8 border-gray-200 bg-gray-50/50 flex flex-col items-center justify-center text-center">
                    <div className="bg-white p-3 rounded-full shadow-sm mb-3">
                      <ExternalLink className="h-6 w-6 text-indigo-600" />
                    </div>
                    <h3 className="font-medium text-gray-900 mb-1">
                      Import from DocSend
                    </h3>
                    <p className="text-xs text-muted-foreground mb-4 max-w-xs">
                      Paste a DocSend link to import the document directly.
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => setIsDocSendPickerOpen(true)}
                      className="gap-2"
                    >
                      Import DocSend Link
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>

              {/* Selected Files List */}
              {files.length > 0 && (
                <div ref={fileListRef} className="space-y-3 pt-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-semibold text-gray-500">
                      Selected Files ({files.length})
                    </Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-xs text-blue-600 hover:text-blue-800 px-0"
                      onClick={() => setFiles([])}
                    >
                      Clear all
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {files.map((file) => {
                      const typesUsedByOthers = new Set(
                        files
                          .filter((f) => f.id !== file.id && f.type)
                          .map((f) => f.type)
                      );
                      return (
                      <div
                        key={file.id}
                        className="flex flex-wrap sm:flex-nowrap items-center gap-2 sm:gap-3 p-3 bg-white border rounded-lg shadow-sm"
                      >
                        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                          <div className="flex-shrink-0">
                            {file.source === "drive" ? (
                              <div className="h-8 w-8 bg-blue-50 rounded flex items-center justify-center text-blue-600">
                                <FileText className="h-4 w-4" />
                              </div>
                            ) : (
                              <div className="h-8 w-8 bg-orange-50 rounded flex items-center justify-center text-orange-600">
                                <FileText className="h-4 w-4" />
                              </div>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 min-w-0">
                              <p
                                className="text-sm font-medium truncate"
                                title={file.name}
                              >
                                {file.name}
                              </p>
                              {file.source === "email" && (
                                <Badge
                                  variant="secondary"
                                  className="shrink-0 text-[10px] h-4 px-1.5"
                                >
                                  From email
                                </Badge>
                              )}
                            </div>
                            {file.size > 0 && (
                              <p className="text-xs text-muted-foreground">
                                {formatBytes(file.size)}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 w-full sm:w-auto pl-10 sm:pl-0">
                          <div className="flex-1 sm:w-[180px] sm:flex-none">
                            <Select
                              value={file.type}
                              onValueChange={(val) =>
                                updateFileType(file.id, val)
                              }
                            >
                              <SelectTrigger
                                className={`h-8 text-xs ${!file.type && "border-orange-300 bg-orange-50"
                                  }`}
                              >
                                <SelectValue placeholder="Select type..." />
                              </SelectTrigger>
                              <SelectContent className="max-h-[280px]">
                                {Object.entries(FILE_TYPE_MAPPING)
                                  .filter(
                                    ([, value]) => !typesUsedByOthers.has(value)
                                  )
                                  .map(([label, value]) => (
                                    <SelectItem
                                      key={value}
                                      value={value}
                                      className="text-xs"
                                    >
                                      {label}
                                    </SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                          </div>
                          {file.source === "email" && file.fileUrl && (
                            <FilePreviewDialog
                              url={file.fileUrl}
                              fileName={file.name}
                              fileId={file.fileUploadId}
                              contentType={file.mimeType}
                            >
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 flex-shrink-0 text-gray-400 hover:text-foreground"
                                aria-label="Preview file"
                                title="Preview file"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </FilePreviewDialog>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 flex-shrink-0 text-gray-400 hover:text-red-500"
                            onClick={() => removeFile(file.id)}
                            aria-label="Remove file"
                            title="Remove file"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                  {files.some((f) => !f.type) && (
                    <div className="bg-orange-50 text-orange-600 text-xs p-2 rounded flex items-center gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-orange-600" />
                      Please select a document type for all files.
                    </div>
                  )}
                </div>
              )}
            </div>

            {isLoading && (
              <div className="space-y-2">
                <Progress value={progress} className="w-full" />
                <p className="text-sm text-muted-foreground text-center">
                  {progress < 25 && "Creating company..."}
                  {files.length > 0 &&
                    progress >= 40 &&
                    progress < 90 &&
                    "Uploading files..."}
                  {progress >= 90 && progress < 100 && "Finalizing deal..."}
                  {progress === 100 && "Complete!"}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="border-t pt-4 px-1">
          <Button
            type="button"
            className="w-full"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleAddDeal(e);
            }}
            disabled={
              !companyName ||
              isLoading ||
              (files.length > 0 && files.some((f) => !f.type))
            }
          >
            {isLoading ? "Adding Deal..." : "Add Deal"}
          </Button>
        </div>
      </DialogContent>

      {activeWorkspaceId && (
        <GoogleDrivePicker
          open={isDrivePickerOpen}
          onClose={() => setIsDrivePickerOpen(false)}
          companyId="" // Not needed for selection mode
          opportunityId="" // Not needed for selection mode
          mode="select"
          onSelectionConfirm={handleDriveSelection}
        />
      )}

      {activeWorkspaceId && (
        <DocSendPicker
          open={isDocSendPickerOpen}
          onClose={() => setIsDocSendPickerOpen(false)}
          companyId="" // Not used in collect mode
          opportunityId={activeWorkspaceId}
          mode="collect"
          onCredentialsCollect={handleDocSendCredentials}
        />
      )}
    </Dialog>
  );
}

function formatBytes(bytes: number, decimals = 2) {
  if (!+bytes) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}
